import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { formatCurrency, ListingBuyerModel, getPricingUnit, formatNumber } from '@steelbuy/ts-shared';
import { Card, CardLayout, FreightQuoteNotification } from '@steelbuy/ui-primitive';
import { shouldShowVat } from '@steelbuy/util';
import { useIsVendorFeatureSupported } from '@steelbuy/vendor';
import { useNumberOfItemsLabel } from '../number-of-items-label/NumberOfItemsLabel';

import './ListingBuyerDetailsPrice.scss';

type ListingBuyerDetailsPriceProps = {
    listing: ListingBuyerModel;
    priceFetchError?: boolean;
    isPriceFetching?: boolean;
};

export const ListingBuyerDetailsPrice = ({
    listing,
    priceFetchError,
    isPriceFetching,
}: ListingBuyerDetailsPriceProps) => {
    const { t } = useTranslation(['uiDomain', 'domainModel', 'translation']);
    const numberOfItemsLabel = useNumberOfItemsLabel(listing.material.product);

    const pricingUnit = getPricingUnit(listing.material);

    const isDynamicDeliveryPriceEnabled = useIsVendorFeatureSupported('DYNAMIC_DELIVERY_PRICE');

    const totalField = isDynamicDeliveryPriceEnabled ? 'totalIncludingVat' : 'totalExcludingVat';

    const acceptedOfferPrice = listing.offerPricing?.priceIncludingDelivery;

    const weightValue = t(`domainModel:material.tradeUnit.value.${listing.tradeUnit}.quantity`, {
        count: listing.weight,
        formatted: formatNumber(listing.weight),
    });

    const applicableTotal = listing.pricing[totalField]?.value;

    const totalOriginalPrice = formatCurrency(applicableTotal, listing.pricing.currencyCode);

    const totalAcceptedOfferPrice = formatCurrency(
        listing.offerPricing?.[totalField]?.value ?? 0,
        listing.pricing.currencyCode
    );

    const showVat = shouldShowVat(listing.pricing.taxPercent);

    const tnaPriceLabel = priceFetchError
        ? t('uiDomain:listingBuyerDetailsPrice.totalExclDelivery')
        : t('uiDomain:listingBuyerDetailsPrice.deliveredTotal');

    const totalPriceLabel = showVat ? t('uiDomain:listingBuyerDetailsPrice.totalExclVat') : tnaPriceLabel;

    const pricePerUnitIncludingDeliveryLabel = t('uiDomain:listingBuyerDetailsPrice.pricePerUnit', {
        pricingUnit: t(`domainModel:material.pricingUnit.value.${pricingUnit}.label`),
    });

    const pricePerUnitLabel = isDynamicDeliveryPriceEnabled
        ? t('uiDomain:listingBuyerDetailsPrice.pricePerUnitExclDelivery', {
              pricingUnit: t(`domainModel:material.pricingUnit.value.${pricingUnit}.label`),
          })
        : pricePerUnitIncludingDeliveryLabel;

    const totalPrice = acceptedOfferPrice ? totalAcceptedOfferPrice : totalOriginalPrice;

    const priceIncludingEstimatedDelivery = listing.pricing?.priceIncludingEstimatedDelivery?.value;

    const showEstimatedDeliveryPrice =
        isDynamicDeliveryPriceEnabled && (priceIncludingEstimatedDelivery > 0 || isPriceFetching);

    return (
        <Card isClickable={false}>
            <CardLayout>
                <article className="listing-buyer-details-price">
                    <article className="listing-buyer-details-price__header">
                        {t('uiDomain:listingBuyerDetailsPrice.cardTitle')}
                    </article>
                    <article className="listing-buyer-details-price__content">
                        <article className="listing-buyer-details-price__content__price-per-unit">
                            <article className="listing-buyer-details-price__content__price-per-unit__item">
                                <span className="listing-buyer-details-price__content__price-per-unit__item__label">
                                    {pricePerUnitLabel}
                                </span>
                                <span
                                    className={classNames(
                                        'listing-buyer-details-price__content__price-per-unit__item__value',
                                        {
                                            'listing-buyer-details-price__content__price-per-unit__item__value--invalid':
                                                acceptedOfferPrice,
                                        }
                                    )}
                                >
                                    {formatCurrency(
                                        listing.pricing.priceIncludingDelivery.value,
                                        listing.pricing.currencyCode
                                    )}
                                </span>
                                {acceptedOfferPrice && (
                                    <span className="listing-buyer-details-price__content__price-per-unit__item__value">
                                        {formatCurrency(acceptedOfferPrice.value, listing.pricing.currencyCode)}
                                    </span>
                                )}
                            </article>
                        </article>
                        {showEstimatedDeliveryPrice && (
                            <article className="listing-buyer-details-price__content__price-per-unit">
                                <article className="listing-buyer-details-price__content__price-per-unit__item">
                                    <span className="listing-buyer-details-price__content__price-per-unit__item__label">
                                        {pricePerUnitIncludingDeliveryLabel}
                                    </span>
                                    <span
                                        className={classNames(
                                            'listing-buyer-details-price__content__price-per-unit__item__value',
                                            {
                                                'listing-buyer-details-price__content__price-per-unit__item__value--invalid':
                                                    acceptedOfferPrice,
                                            }
                                        )}
                                    >
                                        {isPriceFetching || !priceIncludingEstimatedDelivery ? (
                                            <span className="listing-buyer-details-price__content__price-per-unit__item__value--loading">
                                                {t('uiDomain:listingBuyerDetailsPrice.loadingPrice')}
                                                <span />
                                            </span>
                                        ) : (
                                            formatCurrency(
                                                listing.pricing?.priceIncludingEstimatedDelivery?.value,
                                                listing.pricing.currencyCode
                                            )
                                        )}
                                    </span>
                                    {acceptedOfferPrice && (
                                        <span className="listing-buyer-details-price__content__price-per-unit__item__value">
                                            {formatCurrency(
                                                listing.offerPricing?.priceIncludingEstimatedDelivery?.value || 0,
                                                listing.pricing.currencyCode
                                            )}
                                        </span>
                                    )}
                                </article>
                            </article>
                        )}
                        <article className="listing-buyer-details-price__content__total-price">
                            {numberOfItemsLabel && (
                                <article className="listing-buyer-details-price__content__total-price__item">
                                    <span className="listing-buyer-details-price__content__total-price__item__label">
                                        {numberOfItemsLabel}
                                    </span>
                                    <span className="listing-buyer-details-price__content__total-price__item__value">
                                        {listing.numberOfItems}
                                    </span>
                                </article>
                            )}
                            <article className="listing-buyer-details-price__content__total-price__item">
                                <span className="listing-buyer-details-price__content__total-price__item__label">
                                    {t('translation:application.checkout.weight')}
                                </span>
                                <span className="listing-buyer-details-price__content__total-price__item__value">
                                    {weightValue}
                                </span>
                            </article>
                            <article className="listing-buyer-details-price__content__total-price__item">
                                <span className="listing-buyer-details-price__content__total-price__item__label">
                                    {totalPriceLabel}
                                </span>
                                <article className="listing-buyer-details-price__content__total-price__item__value">
                                    {acceptedOfferPrice && (
                                        <span className="listing-buyer-details-price__content__total-price__item__value__original-price">
                                            {totalOriginalPrice}
                                        </span>
                                    )}
                                    <span className="listing-buyer-details-price__content__total-price__item__value__purchase-price">
                                        {isPriceFetching ? (
                                            <span className="listing-buyer-details-price__content__total-price__item__value__purchase-price--loading">
                                                {t('uiDomain:listingBuyerDetailsPrice.loadingPrice')}
                                                <span />
                                            </span>
                                        ) : (
                                            totalPrice
                                        )}
                                    </span>
                                </article>
                            </article>
                        </article>
                    </article>
                    <FreightQuoteNotification
                        isDynamicDeliveryPriceEnabled={isDynamicDeliveryPriceEnabled}
                        error={priceFetchError}
                    />
                </article>
            </CardLayout>
        </Card>
    );
};
